import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface FormValues {
  metric1: string;
  result1: string;
  target1: string;
  metric2: string;
  result2: string;
  goal: string;
}

interface Notes {
  first: string;
  second: string;
}

interface ObservationImage {
  imageUrl: string;
  fileName: string;
  containerName?: string;
  miscImage?: {
    imageUrl: string;
    fileName: string;
    containerName: string;
    description?: string;
  };
  miscImage2?: {
    imageUrl: string;
    fileName: string;
    containerName: string;
    description?: string;
  };
}

interface SelectedOption {
  id: string;
  label: string;
  Pallet_Type?: string;
}

interface PulseImage {
  imageId: string;
  imageBase64: string; // Will store the file name from API response
  imagePreview: string; // Will store the image URL for preview
  description: string;
  observationId: string;
}

interface PulseCheckState {
  selectedOptions: SelectedOption[];
  selectedReferenceQuestions: SelectedOption[];
  formValues: FormValues;
  notes: Notes;
  metGoal: boolean | null;
  observationImages?: ObservationImage[];
  pulseImages: PulseImage[];
  validationErrors: Record<string, boolean>;
  selectedRecord: any | null;
  pulseState: "new" | "in-progress" | "completed";
  fetchedQuestions: any; // Structure with sections of options/questions
  miscImageDescription?: any | null;
  // Signature fields for manager
  managerSignatureFileName: string | null;
  managerSignatureImageUrl: string | null;
  isManagerSignatureUpdated: boolean;
  // Signature fields for employee
  employeeSignatureFileName: string | null;
  employeeSignatureImageUrl: string | null;
  isEmployeeSignatureUpdated: boolean;
  isEmployeeDisclaimerChecked: boolean;
}

const initialState: PulseCheckState = {
  selectedOptions: [],
  selectedReferenceQuestions: [],
  formValues: {
    metric1: "",
    result1: "",
    target1: "",
    metric2: "",
    result2: "",
    goal: "",
  },
  notes: {
    first: "",
    second: "",
  },
  metGoal: null,
  observationImages: [],
  pulseImages: [],
  validationErrors: {},
  selectedRecord: null,
  pulseState: "new",
  fetchedQuestions: [],
  miscImageDescription: {
    description1: "",
    description2: "",
  },
  // Initialize signature fields
  managerSignatureFileName: null,
  managerSignatureImageUrl: null,
  isManagerSignatureUpdated: false,
  employeeSignatureFileName: null,
  employeeSignatureImageUrl: null,
  isEmployeeSignatureUpdated: false,
  isEmployeeDisclaimerChecked: false,
};

const pulseCheckSlice = createSlice({
  name: "pulseCheck",
  initialState,
  reducers: {
    toggleOption(
      state,
      action: PayloadAction<{ id: string; label: string; Pallet_Type?: string }>
    ) {
      const option = action.payload;
      const optionExists = state.selectedOptions.some(
        (item) => item.id === option.id
      );
      if (optionExists) {
        state.selectedOptions = state.selectedOptions.filter(
          (o) => o.id !== option.id
        );
      } else {
        state.selectedOptions.push(option);
      }
    },
    toggleReferenceQuestion(
      state,
      action: PayloadAction<{ id: string; label: string; Pallet_Type?: string }>
    ) {
      const option = action.payload;
      const optionExists = state.selectedReferenceQuestions.some(
        (item) => item.id === option.id
      );
      if (optionExists) {
        state.selectedReferenceQuestions =
          state.selectedReferenceQuestions.filter((o) => o.id !== option.id);
      } else {
        state.selectedReferenceQuestions.push(option);
      }
    },
    setFormValue(
      state,
      action: PayloadAction<{ field: string; value: string }>
    ) {
      const { field, value } = action.payload;
      state.formValues[field as keyof FormValues] = value;
    },
    setNotes(
      state,
      action: PayloadAction<{ which: "first" | "second"; value: string }>
    ) {
      state.notes[action.payload.which] = action.payload.value;
    },
    setMetGoal(state, action: PayloadAction<boolean | null>) {
      state.metGoal = action.payload;
    },
    setObservationImage(
      state,
      action: PayloadAction<{ index: number; image: ObservationImage }>
    ) {
      if (!state.observationImages) {
        state.observationImages = [];
      }
      state.observationImages[action.payload.index] = action.payload.image;
    },
    setSelectedRecord(state, action: PayloadAction<any>) {
      state.selectedRecord = action.payload;
    },
    setPulseState(
      state,
      action: PayloadAction<"new" | "in-progress" | "completed">
    ) {
      state.pulseState = action.payload;
    },
    setfetchedQuestions(state, action: PayloadAction<any>) {
      state.fetchedQuestions = action.payload;
    },
    setMiscImageDescription(
      state,
      action: PayloadAction<{ description1?: string; description2?: string }>
    ) {
      const { description1, description2 } = action.payload;
      if (description1 !== undefined) {
        state.miscImageDescription.description1 = description1;
      }
      if (description2 !== undefined) {
        state.miscImageDescription.description2 = description2;
      }
    },
    setPulseImage(state, action: PayloadAction<PulseImage>) {
      const imageIndex = state.pulseImages.findIndex(
        (img) =>
          img.imageId === action.payload.imageId &&
          img.observationId === action.payload.observationId
      );

      if (imageIndex >= 0) {
        // Update existing image
        state.pulseImages[imageIndex] = action.payload;
      } else {
        // Add new image
        state.pulseImages.push(action.payload);
      }
    },
    resetPulseCheck(state) {
      state.selectedOptions = [];
      state.selectedReferenceQuestions = [];
      state.formValues = { ...initialState.formValues };
      state.notes = { ...initialState.notes };
      state.metGoal = initialState.metGoal;
      state.observationImages = [];
      state.pulseImages = [];
      state.validationErrors = {};
      state.selectedRecord = null;
      state.pulseState = "new";
      state.miscImageDescription = { ...initialState.miscImageDescription };
      // Reset signature fields
      state.managerSignatureFileName = null;
      state.managerSignatureImageUrl = null;
      state.isManagerSignatureUpdated = false;
      state.employeeSignatureFileName = null;
      state.employeeSignatureImageUrl = null;
      state.isEmployeeSignatureUpdated = false;
      state.isEmployeeDisclaimerChecked = false;
    },
    setManagerSignature(
      state,
      action: PayloadAction<{
        fileName: string | null;
        imageUrl?: string | null;
        isNewUpload?: boolean;
      }>
    ) {
      console.log(
        "Redux: Setting manager signature fileName:",
        action.payload.fileName,
        "imageUrl:",
        action.payload.imageUrl
      );
      state.managerSignatureFileName = action.payload.fileName;
      state.managerSignatureImageUrl = action.payload.imageUrl || null;

      // Only set isManagerSignatureUpdated to true if this is a new upload
      if (action.payload.isNewUpload) {
        state.isManagerSignatureUpdated = true;
        console.log("Redux: Manager signature has been newly uploaded");
      } else if (action.payload.isNewUpload === false) {
        // Explicitly set to false when loading from API
        state.isManagerSignatureUpdated = false;
        console.log(
          "Redux: Manager signature loaded from API, not marking as updated"
        );
      }
    },
    setEmployeeSignature(
      state,
      action: PayloadAction<{
        fileName: string | null;
        imageUrl?: string | null;
        isNewUpload?: boolean;
      }>
    ) {
      console.log(
        "Redux: Setting employee signature fileName:",
        action.payload.fileName,
        "imageUrl:",
        action.payload.imageUrl
      );
      state.employeeSignatureFileName = action.payload.fileName;
      state.employeeSignatureImageUrl = action.payload.imageUrl || null;

      // Only set isEmployeeSignatureUpdated to true if this is a new upload
      if (action.payload.isNewUpload) {
        state.isEmployeeSignatureUpdated = true;
        console.log("Redux: Employee signature has been newly uploaded");
      } else if (action.payload.isNewUpload === false) {
        // Explicitly set to false when loading from API
        state.isEmployeeSignatureUpdated = false;
        console.log(
          "Redux: Employee signature loaded from API, not marking as updated"
        );
      }
    },
    resetManagerSignatureFlag(state) {
      state.isManagerSignatureUpdated = false;
      console.log("Redux: Reset isManagerSignatureUpdated flag to false");
    },
    resetEmployeeSignatureFlag(state) {
      state.isEmployeeSignatureUpdated = false;
      console.log("Redux: Reset isEmployeeSignatureUpdated flag to false");
    },
    setEmployeeDisclaimerChecked(state, action: PayloadAction<boolean>) {
      state.isEmployeeDisclaimerChecked = action.payload;
      console.log("Redux: Set isEmployeeDisclaimerChecked to", action.payload);
    },
    setValidationErrors(state, action: PayloadAction<Record<string, boolean>>) {
      state.validationErrors = action.payload;
    },
    clearValidationErrors(state) {
      state.validationErrors = {};
    },
  },
});

export const {
  toggleOption,
  toggleReferenceQuestion,
  setFormValue,
  setNotes,
  setMetGoal,
  resetPulseCheck,
  setObservationImage,
  setMiscImageDescription,
  setValidationErrors,
  clearValidationErrors,
  setSelectedRecord,
  setPulseState,
  setfetchedQuestions,
  setPulseImage,
  // New signature actions
  setManagerSignature,
  setEmployeeSignature,
  resetManagerSignatureFlag,
  resetEmployeeSignatureFlag,
  setEmployeeDisclaimerChecked,
} = pulseCheckSlice.actions;
export default pulseCheckSlice.reducer;
